import { useState, useRef, useCallback } from 'react';
import { formatTime, parseTimestamp } from '../../utils/time';

interface ScrubberProps {
  currentTime: number;
  duration: number;
  progress: number;
  onSeek: (percent: number) => void;
  onSeekToTime?: (seconds: number) => void;
  disabled?: boolean;
  isTransitioning?: boolean;
}

export function Scrubber({
  currentTime,
  duration,
  progress,
  onSeek,
  onSeekToTime,
  disabled = false,
  isTransitioning = false,
}: ScrubberProps) {
  const trackRef = useRef<HTMLDivElement>(null);

  // Drag state — use refs to avoid stale closures in event handlers
  const [isDragging, setIsDragging] = useState(false);
  const [dragPercent, setDragPercent] = useState(0);
  const isDraggingRef = useRef(false);
  const dragPercentRef = useRef(0);

  const displayPercent = isDragging ? dragPercent : progress;
  const displayTime = isDragging ? (dragPercent / 100) * duration : currentTime;
  const remaining = duration - displayTime;

  // Hover preview state
  const [hoverPercent, setHoverPercent] = useState<number | null>(null);
  const [hoverX, setHoverX] = useState(0);

  // Time input state
  const [isTimeInputOpen, setIsTimeInputOpen] = useState(false);
  const [timeInputValue, setTimeInputValue] = useState('');

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const percent = parseFloat(event.target.value);
    if (isDraggingRef.current) {
      dragPercentRef.current = percent;
      setDragPercent(percent);
    } else {
      onSeek(percent);
    }
  }, [onSeek]);

  const handleDragStart = useCallback(() => {
    if (disabled) return;
    isDraggingRef.current = true;
    dragPercentRef.current = progress;
    setIsDragging(true);
    setDragPercent(progress);
  }, [disabled, progress]);

  const handleDragEnd = useCallback(() => {
    if (isDraggingRef.current) {
      onSeek(dragPercentRef.current);
      isDraggingRef.current = false;
      setIsDragging(false);
    }
  }, [onSeek]);

  // Calculate time from hover position
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!trackRef.current || disabled) return;
    const rect = trackRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setHoverPercent(percent);
    setHoverX(x);
  }, [disabled]);

  const handleMouseLeave = useCallback(() => {
    setHoverPercent(null);
  }, []);

  // Time at hover position
  const hoverTime = hoverPercent !== null ? (hoverPercent / 100) * duration : null;

  // Handle time input
  const openTimeInput = useCallback(() => {
    setTimeInputValue(formatTime(displayTime));
    setIsTimeInputOpen(true);
  }, [displayTime]);

  const handleTimeInputSubmit = useCallback(() => {
    const seconds = parseTimestamp(timeInputValue);
    if (seconds !== undefined && seconds >= 0 && seconds <= duration) {
      if (onSeekToTime) {
        onSeekToTime(seconds);
      } else {
        onSeek((seconds / duration) * 100);
      }
    }
    setIsTimeInputOpen(false);
  }, [timeInputValue, duration, onSeek, onSeekToTime]);

  const handleTimeInputKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTimeInputSubmit();
    } else if (e.key === 'Escape') {
      setIsTimeInputOpen(false);
    }
  }, [handleTimeInputSubmit]);

  return (
    <div className="w-full px-2">
      {/* Custom progress track with filled portion */}
      <div
        ref={trackRef}
        className="relative h-10 flex items-center group"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* Background track - contrasting colors for visibility */}
        <div className="absolute inset-x-0 h-1.5 bg-black/20 dark:bg-white/25 rounded-full" />

        {/* Progress fill */}
        <div
          className={`absolute left-0 h-1.5 bg-[var(--color-primary)] rounded-full
                     ${isDragging ? '' : 'transition-all duration-75 ease-out'}`}
          style={{ width: `${displayPercent}%` }}
        />

        {/* Hover preview indicator */}
        {hoverPercent !== null && hoverTime !== null && (
          <>
            {/* Hover line */}
            <div
              className="absolute top-1/2 -translate-y-1/2 w-0.5 h-3 bg-[var(--color-text-muted)]/50
                         pointer-events-none rounded-full"
              style={{ left: `${hoverPercent}%` }}
            />
            {/* Hover time tooltip */}
            <div
              className="absolute bottom-full mb-2 px-2 py-1 text-xs font-medium
                         bg-[var(--color-surface-elevated)] text-[var(--color-text)]
                         rounded-[var(--radius-md)] shadow-lg border border-[var(--color-border)]
                         pointer-events-none whitespace-nowrap tabular-nums
                         transform -translate-x-1/2"
              style={{ left: hoverX }}
            >
              {formatTime(hoverTime)}
            </div>
          </>
        )}

        {/* Range input (invisible, for interaction) */}
        <input
          type="range"
          min="0"
          max="100"
          step="0.1"
          value={displayPercent}
          onChange={handleChange}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          onMouseUp={handleDragEnd}
          onTouchEnd={handleDragEnd}
          disabled={disabled}
          aria-label="Playback position"
          aria-valuemin={0}
          aria-valuemax={duration}
          aria-valuenow={displayTime}
          aria-valuetext={`${formatTime(displayTime)} of ${formatTime(duration)}`}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer
                     disabled:cursor-not-allowed"
        />

        {/* Custom thumb */}
        <div
          className={`absolute w-3 h-3 bg-[var(--color-primary)] rounded-full
                     shadow-md shadow-[var(--color-primary)]/40
                     transform -translate-x-1/2 pointer-events-none
                     transition-transform duration-150 ease-out
                     group-hover:scale-125 group-active:scale-110
                     ${isDragging ? 'scale-125' : ''}`}
          style={{ left: `${displayPercent}%` }}
        />
      </div>

      {/* Time labels */}
      <div className={`flex justify-between px-1 text-xs font-medium
                      text-[var(--color-text-muted)] tabular-nums
                      transition-opacity duration-300 ease-in-out
                      ${isTransitioning ? 'opacity-30' : 'opacity-100'}`}>
        {/* Current time - tappable to enter specific time */}
        {isTimeInputOpen ? (
          <input
            type="text"
            value={timeInputValue}
            onChange={(e) => setTimeInputValue(e.target.value)}
            onKeyDown={handleTimeInputKeyDown}
            onBlur={handleTimeInputSubmit}
            autoFocus
            placeholder="0:00:00"
            className="w-20 px-1.5 py-0.5 text-xs font-medium tabular-nums
                       bg-[var(--color-surface-elevated)] text-[var(--color-text)]
                       border border-[var(--color-primary)] rounded-[var(--radius-sm)]
                       focus:outline-none"
          />
        ) : (
          <button
            onClick={openTimeInput}
            className="px-1.5 py-0.5 -ml-1.5 rounded-[var(--radius-sm)]
                       hover:bg-[var(--color-surface-elevated)]
                       hover:text-[var(--color-text)]
                       transition-colors focus:outline-none
                       focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
            title="Click to enter a specific time"
          >
            {formatTime(displayTime)}
          </button>
        )}
        <span id="remaining-time">-{formatTime(remaining)}</span>
      </div>
    </div>
  );
}
